import { useState } from 'react'
import { useAuth } from '../context/auth'
import { hasRole, getAllRoles } from '../lib/roleUtils'
import { User, Truck, Wrench, Package, Building2, Shield, ChevronDown } from 'lucide-react'
import CustomerDashboard from '../dashboards/CustomerDashboard'
import TowerDashboard from '../dashboards/TowerDashboard'
import GarageDashboard from '../dashboards/GarageDashboard'
import PartsVendorDashboard from '../dashboards/PartsVendorDashboard'
import InsurerDashboard from '../dashboards/InsurerDashboard'
import AdminDashboard from '../dashboards/AdminDashboard'

type DashboardTab = 'customer' | 'tower' | 'garage' | 'parts_vendor' | 'insurer' | 'admin'

const DASHBOARD_CONFIG: Record<DashboardTab, { label: string; icon: typeof User; color: string }> = {
  customer: { label: 'Customer', icon: User, color: '#dc2626' },
  tower: { label: 'Tower', icon: Truck, color: '#1e40af' },
  garage: { label: 'Garage', icon: Wrench, color: '#ea580c' },
  parts_vendor: { label: 'Parts Shop', icon: Package, color: '#059669' },
  insurer: { label: 'Insurer', icon: Building2, color: '#7c3aed' },
  admin: { label: 'Admin', icon: Shield, color: '#0891b2' },
}

export default function UnifiedDashboard() {
  const { profile } = useAuth()
  const [activeTab, setActiveTab] = useState<DashboardTab | null>(null)

  if (!profile) return null

  const userRoles = getAllRoles(profile)

  // Get preferred role from localStorage or use first role
  const preferredRole = localStorage.getItem('preferredRole') as DashboardTab | null
  const validPreferredRole = preferredRole && userRoles.includes(preferredRole) ? preferredRole : null

  // Set initial tab
  if (!activeTab) {
    setActiveTab(validPreferredRole || (userRoles[0] as DashboardTab))
  }

  // If user has only one role, show that dashboard directly
  if (userRoles.length === 1) {
    const singleRole = userRoles[0] as DashboardTab
    switch (singleRole) {
      case 'customer': return <CustomerDashboard />
      case 'tower': return <TowerDashboard />
      case 'garage': return <GarageDashboard />
      case 'parts_vendor': return <PartsVendorDashboard />
      case 'insurer': return <InsurerDashboard />
      case 'admin': return <AdminDashboard />
      default: return <CustomerDashboard />
    }
  }

  // Multi-role user - show selected dashboard
  const handleTabChange = (role: DashboardTab) => {
    setActiveTab(role)
    localStorage.setItem('preferredRole', role)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Dashboard Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '1.5rem' }}>
        {activeTab === 'customer' && <CustomerDashboard />}
        {activeTab === 'tower' && <TowerDashboard />}
        {activeTab === 'garage' && <GarageDashboard />}
        {activeTab === 'parts_vendor' && <PartsVendorDashboard />}
        {activeTab === 'insurer' && <InsurerDashboard />}
        {activeTab === 'admin' && <AdminDashboard />}
      </div>
    </div>
  )
}
